import { z } from 'zod';
import { SupportType } from './Funnel';
import { FunnelStageConfigDTO } from './FunnelStageConfig';
import {
  exampleFunnelStageGroupConfig,
  FunnelStageGroupConfigDTO,
} from './FunnelStageGroupConfig';

/**
 * A funnel stage as stored inside a user's template.
 *
 * Reuses the business-funnel stage schema rather than a parallel one, with two
 * deliberate differences: no `id` on the stage or on its groups, and a relaxed
 * `conversion_name`. A template is applied to a *different* business, so a
 * surviving id would make the business-save path treat a template stage as an
 * update to a row owned by another business.
 */
export const FunnelTemplateStageDTO = FunnelStageConfigDTO.omit({
  id: true,
  conversion_name: true,
  groups: true,
}).extend({
  // Deliberately NOT FunnelStageConfigDTO's `.refine(str => str !== '')`.
  // Saving a template is a checkpoint, not a publish — the built-in "Custom"
  // preset itself ships conversion_name: ''. BusinessConfigDTO still enforces
  // non-empty + unique conversion names on the business save, which is where it
  // actually matters. Don't "fix" this back.
  conversion_name: z.string(),
  groups: z.array(FunnelStageGroupConfigDTO.omit({ id: true })),
});

export type FunnelTemplateStage = z.TypeOf<typeof FunnelTemplateStageDTO>;

/**
 * Source keys that only mean something inside one business. A custom
 * conversion id belongs to one Meta ad account; an insights key is an action
 * type the ad account has to have recorded. Every other key (`impressions`,
 * `fbleads`, `rccalls*`, `fbpixel/<Event>`, `offline_events/<Event>`) is global.
 */
export const isPerBusinessSource = (source: string): boolean =>
  source.startsWith('custom_conversions/') || source.startsWith('insights/');

export const CreateFunnelTemplateDTO = z.object({
  name: z.string().trim().min(1).max(120),
  desc: z.string().trim().max(500).default(''),
  icon: z.string().trim().max(64).default('fas fa-layer-group'),
  stages: z.array(FunnelTemplateStageDTO).min(1).max(30),
  // Display names for the per-business sources, captured from the business the
  // template was saved from. The business it is applied to may not have those
  // sources, so without this the apply warning could only show raw ids.
  sourceNames: z
    .record(z.string().trim().max(255))
    .refine((names) => Object.keys(names).length <= 500)
    .default({}),
});

export type CreateFunnelTemplate = z.TypeOf<typeof CreateFunnelTemplateDTO>;

export interface FunnelTemplate {
  id: number;
  name: string;
  /**
   * `desc`, not `description`, on purpose: it matches the admin's `Preset`
   * field name, so the picker can show templates and built-ins side by side.
   * The shapes still differ inside `stages` (`est_conversion_name` is nullable
   * here), so admin maps through `presetFromTemplate` rather than spreading.
   */
  desc: string;
  icon: string;
  stages: FunnelTemplateStage[];
  /** Source key -> display name, for per-business sources only. */
  sourceNames: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

/**
 * Template ids are offset before they enter the funnel preset picker, so the
 * four built-in presets keep 1–4 and `preset === 0` still means "show the
 * grid". `setPreset`'s `.find()` then resolves either kind with no signature
 * change.
 *
 * The offset id is persisted to `businesses.funnel_preset` (an `int4`, max
 * ~2.1B) on the next business save, leaving room for ~2.1B templates. Nothing
 * on the backend resolves the value — it is provenance and grid highlight only.
 *
 * Stale ids are expected and safe. If the template is later deleted, or the
 * business is opened by a *different* user, the `.find()` simply misses: the
 * card is not highlighted, `preset !== 0` so the editor still renders from the
 * persisted `funnel_stages` rows, and `setPreset`'s own guard means nothing
 * throws.
 */
export const FUNNEL_TEMPLATE_PRESET_ID_OFFSET = 1_000_000;

export const funnelTemplatePresetID = (id: number): number =>
  FUNNEL_TEMPLATE_PRESET_ID_OFFSET + id;

export const funnelTemplateIDFromPresetID = (presetID: number): number | null =>
  presetID > FUNNEL_TEMPLATE_PRESET_ID_OFFSET
    ? presetID - FUNNEL_TEMPLATE_PRESET_ID_OFFSET
    : null;

/**
 * Single source of truth for live-funnel-state -> template stage. Called by the
 * admin dialog before POST, and again by FunnelTemplatesService.create so a
 * handcrafted request cannot bypass it.
 *
 * Input is typed loosely on purpose: admin funnel state is `any` throughout
 * StageActions, and importing BusinessManagerStage here would risk a
 * business <-> funnel import cycle.
 *
 * `data_sources_id`, `campaign_goal`, `stage_target`, `support` and each
 * group's `sources` are kept verbatim — all but `sources` are global, and
 * `sources` is checked against the target business on *apply* (and the user
 * confirms any removals) rather than here, so a template keeps working inside
 * the account family it was saved in.
 */
export function toFunnelTemplateStages(
  stages: readonly Record<string, any>[],
): FunnelTemplateStage[] {
  const normalized = [...stages]
    // Sort by the order the user sees, then reassign a dense 0-based one. Three
    // conventions collide in live state: built-in presets are 1-based,
    // `changeOrder` rewrites to 0-based, and `addStage` pushes `order: 100`.
    // Without this an applied template lands in a different order than saved.
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map(({ id, groups, ...stage }, index) => ({
      ...stage,
      order: index,
      groups: ((groups ?? []) as Record<
        string,
        any
      >[]).map(({ id: groupID, ...group }) => ({ ...group })),
    }));

  // Parsed last so a malformed funnel fails in the dialog, not as a 400.
  return FunnelTemplateStageDTO.array().parse(normalized);
}

/**
 * Keeps only the names the template can use: per-business keys that its stages
 * actually reference, with a non-blank name. Run on both client and server, like
 * toFunnelTemplateStages.
 */
export function toFunnelTemplateSourceNames(
  stages: readonly FunnelTemplateStage[],
  names: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const stage of stages) {
    for (const group of stage.groups) {
      for (const source of group.sources) {
        const name = names[source]?.trim();
        if (isPerBusinessSource(source) && name) {
          result[source] = name;
        }
      }
    }
  }
  return result;
}

export const exampleFunnelTemplate: FunnelTemplate = {
  id: 1,
  name: 'Funnel Template',
  desc: 'Description',
  icon: 'fas fa-layer-group',
  stages: [
    {
      name: 'Funnel Stage',
      desc: 'Description',
      icon: 'fa-person',
      support: [SupportType.Conversions],
      groups: [exampleFunnelStageGroupConfig],
      estimate: true,
      order: 0,
      conversion_name: 'Conversions',
      est_conversion_name: 'Estimated Conversions',
      show_total: true,
      is_kpi: true,
      is_sub_kpi: false,
      show_overview: true,
      cost_per_metric: 1,
    },
  ],
  sourceNames: {},
  createdAt: '2026-09-22T00:00:00.000Z',
  updatedAt: '2026-09-22T00:00:00.000Z',
};
